import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  FloorId,
  FloorPlanId,
  JobRoleId,
  MaterialId,
  ProjectId,
  RoomId,
  ServiceId,
  TakeoffLineId,
  TowerId,
  UnitId,
  UnitLineOverrideId,
  UnitOfMeasureId,
  UnitTypeId,
  UserId,
} from '../shared/ids';
import type { JobRoleDisciplineIndex } from '../workforce/job-role-discipline';
import { MAN_HOUR_UNIT_CODE, roundQuantity, type UnitOfMeasure } from '../schedule/unit-of-measure';
import { itemNotAllowedError, type Service, type ServiceItemIndex } from './service';
import { TAKEOFF_ERROR, TAKEOFF_ERROR_MESSAGE } from './errors';

/**
 * A linha do quantitativo.
 *
 * Lê-se: **tanto** deste item, no serviço X, neste lugar. O lugar é qualquer nó da obra
 * (RN-174) — garagem e canteiro não têm ambiente e têm orçamento —, e o normal é ele ser
 * a tipologia ou um ambiente dela, porque é lá que o número mora (RN-167).
 */

export type TakeoffOwner =
  | { readonly kind: 'ROOM'; readonly roomId: RoomId }
  | { readonly kind: 'UNIT_TYPE'; readonly unitTypeId: UnitTypeId }
  /** Linha que existe **só neste apto** — o acréscimo do 1204, sem tocar na tipologia. */
  | { readonly kind: 'UNIT'; readonly unitId: UnitId }
  | { readonly kind: 'FLOOR_PLAN'; readonly floorPlanId: FloorPlanId }
  | { readonly kind: 'FLOOR'; readonly floorId: FloorId }
  | { readonly kind: 'TOWER'; readonly towerId: TowerId }
  | { readonly kind: 'PROJECT'; readonly projectId: ProjectId };

/** As quatro classes que vêm do catálogo de compras. */
export type CatalogItemKind = 'MATERIAL' | 'INSUMO' | 'FERRAMENTA' | 'EQUIPAMENTO';

/** As cinco que a obra lança — a quinta não é produto de compras, é gente. */
export type ItemKind = CatalogItemKind | 'MAO_DE_OBRA';

/**
 * Vira pedido de compra?
 *
 * Mão de obra não: ela sai em homem-hora e vai para o planejamento. Misturá-la à lista de
 * material poria hora de pedreiro num pedido de fornecedor (RN-166).
 */
export function isPurchasable(kind: ItemKind): boolean {
  return kind !== 'MAO_DE_OBRA';
}

export const ITEM_KIND_LABEL: Readonly<Record<ItemKind, string>> = {
  MATERIAL: 'Material',
  INSUMO: 'Insumo',
  FERRAMENTA: 'Ferramenta',
  EQUIPAMENTO: 'Equipamento',
  MAO_DE_OBRA: 'Mão de obra',
};

/**
 * De onde veio a quantidade (RN-171).
 *
 * `MANUAL` é quem digitou. `COMPOSITION` é a composição, que ainda não chegou.
 * `IFC` é o modelo: a viga que o projetista desenhou disse quantos metros cúbicos ela
 * tem, e ninguém precisou contar.
 *
 * O campo existe desde antes de ter três valores, e por um motivo que agora se paga: sem
 * ele, a reimportação do modelo teria de escolher entre **atropelar** o número que alguém
 * decidiu na mão e **não recalcular nada**. Com ele, a reimportação mexe no que veio do
 * modelo e não encosta no resto — e é essa distinção que o índice único parcial do banco
 * usa, restringindo a chave natural a `origin = 'IFC'`.
 */
export type QuantityOrigin = 'MANUAL' | 'COMPOSITION' | 'IFC';

export interface TakeoffLine {
  readonly id: TakeoffLineId;
  readonly projectId: ProjectId;
  readonly owner: TakeoffOwner;
  readonly serviceId: ServiceId;
  /** Presente exatamente quando a linha **não** é de mão de obra (RN-166). */
  readonly materialId?: MaterialId;
  /** Presente exatamente quando a linha é de mão de obra (RN-166). */
  readonly jobRoleId?: JobRoleId;
  readonly quantity: number;
  /**
   * Unidade de **medida**.
   *
   * Escrito por extenso, e não `unitId` como no item de cronograma, porque neste módulo
   * "unit" já é o apto. Duas coisas com o mesmo nome no mesmo arquivo é como se troca
   * homem-hora por metro quadrado sem ninguém perceber.
   */
  readonly unitOfMeasureId: UnitOfMeasureId;
  readonly origin: QuantityOrigin;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateTakeoffLineInput {
  readonly id: TakeoffLineId;
  readonly projectId: ProjectId;
  readonly owner: TakeoffOwner;
  readonly service: Pick<Service, 'id' | 'disciplineId'>;
  readonly materialId?: MaterialId;
  readonly jobRoleId?: JobRoleId;
  readonly quantity: number;
  readonly unit: UnitOfMeasure;
  readonly origin?: QuantityOrigin;
  readonly notes?: string;
  readonly now: string;
}

export interface TakeoffLineRules {
  /** O que cada serviço autoriza (RN-165). */
  readonly serviceItems: ServiceItemIndex;
  /** Que função executa que disciplina (RN-166) — o vínculo global da Fase 6. */
  readonly roleDisciplines: JobRoleDisciplineIndex;
}

/**
 * Cria a linha, com as três travas que a fase inteira depende.
 *
 * As regras entram por parâmetro em vez de virem de um repositório porque isto é domínio:
 * quem carrega os vínculos é o caso de uso, e o teste consegue montar a situação inteira
 * sem banco nenhum.
 */
export function createTakeoffLine(
  input: CreateTakeoffLineInput,
  rules: TakeoffLineRules,
): Result<TakeoffLine, DomainError> {
  const isLabor = input.jobRoleId !== undefined;
  if (isLabor === (input.materialId !== undefined)) {
    return err(domainError(TAKEOFF_ERROR.ITEM_ARC, TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.ITEM_ARC]!));
  }

  const quantity = roundQuantity(input.quantity, input.unit.decimals);
  // Arredondar antes de comparar: 0,004 HH não é "um pouco de servente", é zero.
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_QUANTITY,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.INVALID_QUANTITY]!,
        { quantity: input.quantity },
      ),
    );
  }

  if (input.materialId !== undefined) {
    if (!rules.serviceItems.allows(input.service.id, input.materialId)) {
      return err(itemNotAllowedError(input.service.id, input.materialId));
    }
  }

  if (input.jobRoleId !== undefined) {
    if (input.unit.code !== MAN_HOUR_UNIT_CODE) {
      return err(
        domainError(TAKEOFF_ERROR.LABOR_UNIT, TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.LABOR_UNIT]!, {
          unit: input.unit.code,
        }),
      );
    }
    if (!rules.roleDisciplines.matches(input.jobRoleId, input.service.disciplineId)) {
      return err(
        domainError(
          TAKEOFF_ERROR.ROLE_WRONG_DISCIPLINE,
          TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.ROLE_WRONG_DISCIPLINE]!,
          { jobRoleId: input.jobRoleId, disciplineId: input.service.disciplineId },
        ),
      );
    }
  }

  return ok({
    id: input.id,
    projectId: input.projectId,
    owner: input.owner,
    serviceId: input.service.id,
    quantity,
    unitOfMeasureId: input.unit.id,
    origin: input.origin ?? 'MANUAL',
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.materialId === undefined ? {} : { materialId: input.materialId }),
    ...(input.jobRoleId === undefined ? {} : { jobRoleId: input.jobRoleId }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

/**
 * O local da linha, como texto.
 *
 * O dono é uma união de sete formas, cada uma com o nome do campo diferente (RN-174).
 * Comparar dois donos exige achatá-los, e achatar com o `kind` na frente evita o acidente
 * óbvio: um ambiente e um pavimento com o mesmo id seriam o mesmo local sem ele.
 */
export function takeoffOwnerKey(owner: TakeoffOwner): string {
  switch (owner.kind) {
    case 'ROOM':
      return `ROOM|${owner.roomId}`;
    case 'UNIT_TYPE':
      return `UNIT_TYPE|${owner.unitTypeId}`;
    case 'UNIT':
      return `UNIT|${owner.unitId}`;
    case 'FLOOR_PLAN':
      return `FLOOR_PLAN|${owner.floorPlanId}`;
    case 'FLOOR':
      return `FLOOR|${owner.floorId}`;
    case 'TOWER':
      return `TOWER|${owner.towerId}`;
    case 'PROJECT':
      return `PROJECT|${owner.projectId}`;
  }
}

/**
 * Chave natural da linha nascida de modelo: uma por (local, serviço, item).
 *
 * Vale **só** para `origin = 'IFC'`, e o índice do banco é parcial pela mesma razão: duas
 * linhas do mesmo tijolo no mesmo ambiente são legítimas quando alguém as digitou
 * ("parede 1", "parede 2"), e proibi-las tornaria a trava da reimportação um estorvo no
 * lançamento à mão.
 *
 * Como o serviço já é filho da disciplina (RN-164), esta chave é, para a linha do
 * quantitativo, o mesmo grão que `(cronograma, atividade, material)` é para a do
 * cronograma — as duas partições vistas de lados diferentes.
 */
export function takeoffLineNaturalKey(
  owner: TakeoffOwner,
  serviceId: ServiceId,
  materialId: MaterialId,
): string {
  return `${takeoffOwnerKey(owner)}|${serviceId}|${materialId}`;
}

/**
 * A classe do item.
 *
 * Mão de obra sai do próprio arco da linha; as outras quatro são propriedade do **item de
 * catálogo**, não do lançamento — o mesmo cimento não pode ser material numa linha e
 * insumo na outra. Enquanto o catálogo de compras não chega, o chamador informa o que
 * sabe e a ausência vale `MATERIAL`.
 */
export function itemKindOf(
  line: Pick<TakeoffLine, 'jobRoleId'>,
  material?: { readonly kind?: CatalogItemKind },
): ItemKind {
  if (line.jobRoleId !== undefined) return 'MAO_DE_OBRA';
  return material?.kind ?? 'MATERIAL';
}

// ── A divergência do apto ───────────────────────────────────────────────────────

/**
 * O que o apto faz com uma linha que herdou (RN-167, RN-168).
 *
 * `REPLACE` troca a quantidade; `REMOVE` diz que ali aquilo não acontece. **Acrescentar**
 * não precisa de registro próprio: é uma linha com dono `UNIT`, e o local já podia ser
 * qualquer nó (RN-174).
 */
export type OverrideEffect = 'REPLACE' | 'REMOVE';

export interface UnitLineOverride {
  readonly id: UnitLineOverrideId;
  readonly unitId: UnitId;
  readonly lineId: TakeoffLineId;
  readonly effect: OverrideEffect;
  /** Presente exatamente quando `effect = REPLACE`. */
  readonly quantity?: number;
  /** Obrigatório: é o que explica o número diferente seis meses depois (RN-168). */
  readonly reason: string;
  readonly by: UserId;
  readonly at: string;
}

export interface CreateUnitLineOverrideInput {
  readonly id: UnitLineOverrideId;
  readonly unitId: UnitId;
  readonly lineId: TakeoffLineId;
  readonly effect: OverrideEffect;
  readonly quantity?: number;
  readonly reason: string;
  readonly by: UserId;
  readonly unit: UnitOfMeasure;
  readonly now: string;
}

export function createUnitLineOverride(
  input: CreateUnitLineOverrideInput,
): Result<UnitLineOverride, DomainError> {
  const reason = input.reason.trim();
  if (reason.length === 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.OVERRIDE_REASON_REQUIRED,
        TAKEOFF_ERROR_MESSAGE[TAKEOFF_ERROR.OVERRIDE_REASON_REQUIRED]!,
      ),
    );
  }

  if (input.effect === 'REMOVE') {
    return ok({
      id: input.id,
      unitId: input.unitId,
      lineId: input.lineId,
      effect: 'REMOVE',
      reason,
      by: input.by,
      at: input.now,
    });
  }

  const quantity = roundQuantity(input.quantity ?? 0, input.unit.decimals);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return err(
      domainError(
        TAKEOFF_ERROR.INVALID_QUANTITY,
        'Para zerar a quantidade neste apto, remova a linha em vez de lançar zero.',
        { quantity: input.quantity },
      ),
    );
  }

  return ok({
    id: input.id,
    unitId: input.unitId,
    lineId: input.lineId,
    effect: 'REPLACE',
    quantity,
    reason,
    by: input.by,
    at: input.now,
  });
}

/** De onde veio a quantidade que este apto usa. */
export type ResolvedSource = 'TYPE' | 'OVERRIDE' | 'UNIT';

export interface ResolvedLine {
  readonly line: TakeoffLine;
  readonly quantity: number;
  readonly source: ResolvedSource;
  /** A divergência que produziu este número, quando houve uma. */
  readonly override?: UnitLineOverride;
}

export interface ResolveUnitLinesInput {
  /** As linhas da tipologia — e dos ambientes dela. */
  readonly inherited: readonly TakeoffLine[];
  /** As linhas que só existem neste apto (dono `UNIT`). */
  readonly own?: readonly TakeoffLine[];
  readonly overrides?: readonly UnitLineOverride[];
}

/**
 * O quantitativo de um apto: **a tipologia, menos as sombras** (RN-167).
 *
 * Ausência de sombra é o caso comum e significa "segue a tipologia" — é o que faz o
 * orçamento de vinte andares caber em algumas centenas de linhas. A sombra é a exceção do
 * 1204, e ela vem marcada, para que a tela mostre que aquele número foi decidido e por
 * quem.
 */
export function resolveUnitLines(input: ResolveUnitLinesInput): readonly ResolvedLine[] {
  const overrideByLine = new Map<TakeoffLineId, UnitLineOverride>(
    (input.overrides ?? []).map((override) => [override.lineId, override]),
  );

  const resolved: ResolvedLine[] = [];

  for (const line of input.inherited) {
    if (line.deletedAt !== undefined) continue;
    const override = overrideByLine.get(line.id);
    if (override === undefined) {
      resolved.push({ line, quantity: line.quantity, source: 'TYPE' });
      continue;
    }
    if (override.effect === 'REMOVE') continue;
    resolved.push({
      line,
      quantity: override.quantity!,
      source: 'OVERRIDE',
      override,
    });
  }

  for (const line of input.own ?? []) {
    if (line.deletedAt !== undefined) continue;
    resolved.push({ line, quantity: line.quantity, source: 'UNIT' });
  }

  return resolved;
}

/** Este apto foge da tipologia? É o que a tela de propagação lista um a um (RN-169). */
export function unitDiverges(input: ResolveUnitLinesInput): boolean {
  return (input.overrides?.length ?? 0) > 0 || (input.own?.length ?? 0) > 0;
}

/**
 * Quais aptos divergem **nesta** linha.
 *
 * É a pergunta que a propagação faz antes de aplicar: quem, entre os vinte, decidiu outra
 * coisa aqui e não pode ser atropelado em silêncio (RN-169).
 */
export function unitsDivergingOn(
  lineId: TakeoffLineId,
  overrides: readonly UnitLineOverride[],
): readonly UnitId[] {
  return overrides
    .filter((override) => override.lineId === lineId)
    .map((override) => override.unitId);
}
